import { createAxiosInstance } from "api/axiosConfig";
import { DOWNLOAD_DOCUMENT } from "api/Service";

/**
 * Builds the filename for download based on fileName and fileType
 * If fileName already contains the extension (fileType), it won't be added again
 * @param {string} fileName - The name of the file (REQUIRED)
 * @param {string} fileType - The file extension (e.g., 'pdf', 'png', 'jpg') (REQUIRED)
 * @returns {string} - The formatted filename
 * @throws {Error} - If fileName or fileType is not provided
 */
export const buildDownloadFileName = (fileName, fileType) => {
  if (!fileName) {
    throw new Error("fileName is required for document download");
  }

  if (!fileType) {
    throw new Error("fileType is required for document download");
  }

  const lowerFileName = fileName.toLowerCase();
  const lowerFileType = fileType.toLowerCase();

  const extensionToCheck = lowerFileType.startsWith('.')
    ? lowerFileType
    : `.${lowerFileType}`;

  if (lowerFileName.endsWith(extensionToCheck)) {
    return fileName;
  }

  return `${fileName}.${lowerFileType.replace(/^\./, '')}`;
};

export const fetchDocumentBlob = async (documentId, patientId, tenant) => {
  if (!documentId || !patientId) {
    console.warn("Missing documentId or patientId for download");
    return null;
  }

  const axiosInstance = createAxiosInstance();
  const response = await axiosInstance.get(
    `${tenant}${DOWNLOAD_DOCUMENT.replace("{documentId}", documentId).replace(
      "{patientId}",
      patientId
    )}`,
    {
      headers: {
        "Content-Type": "application/octet-stream",
      },
      responseType: "blob",
    }
  );

  if (response.data instanceof Blob) {
    return response.data;
  }

  return null;
};

/**
 * Downloads a single document and triggers browser download
 * @param {Object} params - Download parameters
 * @param {string|number} params.documentId - The document ID (REQUIRED)
 * @param {string|number} params.patientId - The patient ID (REQUIRED)
 * @param {string} params.tenant - The tenant value (REQUIRED)
 * @param {string} params.fileName - File name (REQUIRED)
 * @param {string} params.fileType - File type/extension (REQUIRED)
 * @returns {Promise<boolean>} - True if download was successful
 * @throws {Error} - If required parameters are missing
 */
export const downloadDocument = async ({
  documentId,
  patientId,
  tenant,
  fileName,
  fileType,
}) => {

  if (!documentId) {
    throw new Error("documentId is required for document download");
  }
  if (!patientId) {
    throw new Error("patientId is required for document download");
  }
  if (!tenant) {
    throw new Error("tenant is required for document download");
  }
  if (!fileName) {
    throw new Error("fileName is required for document download");
  }
  if (!fileType) {
    throw new Error("fileType is required for document download");
  }

  try {
    const blob = await fetchDocumentBlob(documentId, patientId, tenant);

    if (!blob) {
      return false;
    }

    const downloadUrl = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", buildDownloadFileName(fileName, fileType));
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return true;
  } catch (error) {
    console.error("Error downloading document:", error);
    throw error;
  }
};

/**
 * Downloads multiple documents
 * @param {Array} documents - Array of document objects with id, patientId, fileName, fileType (all REQUIRED)
 * @param {string} tenant - The tenant value (REQUIRED)
 * @param {Function} [onProgress] - Optional callback for progress updates (index, total)
 * @returns {Promise<{success: number, failed: number, errors: Array}>} - Results of bulk download
 * @throws {Error} - If tenant is not provided
 */
export const downloadDocumentsBulk = async (documents, tenant, onProgress) => {
  if (!tenant) {
    throw new Error("tenant is required for bulk document download");
  }

  const results = { success: 0, failed: 0, errors: [] };

  for (let i = 0; i < documents.length; i++) {
    const doc = documents[i];
    const documentId = doc.id || doc.documentId || doc.key;
    const patientId = doc.patientId || doc.patient?.id;
    const fileName = doc.fileName || doc.name;
    const fileType = doc.fileType || doc.type;

    if (!documentId || !patientId || !fileName || !fileType) {
      const missingFields = [];
      if (!documentId) missingFields.push("documentId");
      if (!patientId) missingFields.push("patientId");
      if (!fileName) missingFields.push("fileName");
      if (!fileType) missingFields.push("fileType");

      results.failed++;
      results.errors.push({
        documentId: documentId || "unknown",
        error: `Missing required fields: ${missingFields.join(", ")}`,
      });
      continue;
    }

    try {
      const success = await downloadDocument({
        documentId,
        patientId,
        tenant,
        fileName,
        fileType,
      });

      if (success) {
        results.success++;
      } else {
        results.failed++;
        results.errors.push({
          documentId,
          error: "Download returned false",
        });
      }
    } catch (error) {
      console.error(`Failed to download document ${documentId}:`, error);
      results.failed++;
      results.errors.push({
        documentId,
        error: error.message,
      });
    }

    if (onProgress) {
      onProgress(i + 1, documents.length);
    }

    if (i < documents.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  }

  return results;
};

export const fetchDocumentForPreview = async (documentId, patientId, tenant) => {
  try {
    const blob = await fetchDocumentBlob(documentId, patientId, tenant);

    if (!blob) {
      return null;
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  } catch (error) {
    console.error("Error fetching document for preview:", error);
    throw error;
  }
};